import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'
import { getWimsConstants } from 'features/Product/services/productAdd'
import { getProductDetailApi } from 'features/Product/services/productMasterDetail'
import { getProductSEOApi, updateProductSEOApi } from 'features/Product/services/seo'
import { getBuyerLandingProductPageURL } from 'features/Product/utils/product'
import {
  SEODetailResponseType,
  SEOFormType,
  SEOPlaceholderType,
  SEORobotsType,
  SEOUpdatePayloadType,
  SlugChangeType,
  SlugFormType,
} from 'features/Product/@types/SEO'

export const SLICE_NAME = 'product/seo'

// robots_tag comes as a comma list, e.g. "index, follow" / "noindex, nofollow".
// Missing tag => keep default (index + follow).
const parseRobotsTag = (robotsTag?: string): SEORobotsType => {
  if (!robotsTag) return { index: true, follow: true }
  const tags = robotsTag.split(',').map((tag) => tag.trim().toLowerCase())
  return {
    index: tags.includes('index'),
    follow: tags.includes('follow'),
  }
}

// robots form back to comma list expected by the API, e.g. "index, follow" / "noindex, nofollow".
const serializeRobotsTag = (robots: SEORobotsType): string =>
  [robots.index ? 'index' : 'noindex', robots.follow ? 'follow' : 'nofollow'].join(', ')

// product_description is stored as html, meta description must be plain text.
const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

// slug_recommendation response => SlugForm; changes list only holds fields that differ.
// empty current_slug => fall back to the product detail slug.
const mapSlugRecommendation = (
  recommendation: NonNullable<SEODetailResponseType['slug_recommendation']>,
  fallbackSlug: string,
): SlugFormType => {
  const changes: SlugChangeType[] = []
  if (recommendation.previous_product_name !== recommendation.current_product_name) {
    changes.push({
      field: 'Nama Produk',
      before: recommendation.previous_product_name,
      after: recommendation.current_product_name,
    })
  }
  if (recommendation.previous_grammation !== recommendation.current_grammation) {
    changes.push({
      field: 'Gramasi',
      before: recommendation.previous_grammation,
      after: recommendation.current_grammation,
    })
  }

  return {
    currentSlug: getBuyerLandingProductPageURL(recommendation.current_slug || fallbackSlug),
    label: recommendation.recommended_slug,
    domain: getBuyerLandingProductPageURL(''),
    changes,
  }
}

export const fetchProductDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchProductDetail`,
  async (productId: number) => {
    try {
      const {
        data: { data },
      } = await getProductDetailApi(productId)
      return data
    } catch (error) {
      callErrorMsg(error)
      return null
    }
  },
)

export const fetchSEOOptions = createAsyncThunk(`${SLICE_NAME}/fetchSEOOptions`, async () => {
  try {
    const {
      data: { data },
    } = await getWimsConstants()
    return (data.product_seo_placeholders ?? []) as SEOPlaceholderType[]
  } catch (error) {
    callErrorMsg(error)
    return [] as SEOPlaceholderType[]
  }
})

export const fetchSEODetail = createAsyncThunk(
  `${SLICE_NAME}/fetchSEODetail`,
  async (productId: number, { getState, rejectWithValue }) => {
    try {
      const {
        data: {
          data: { seo, slug_recommendation: slugRecommendation },
        },
      } = await getProductSEOApi(productId)

      // empty canonical_url => fall back to buyer landing page url from product slug.
      const {
        seo: { productDetail },
      } = getState() as StoreStateType
      const url = seo.canonical_url || getBuyerLandingProductPageURL(productDetail?.slug ?? '')

      const form: Partial<SEOFormType> = {
        metaTitle: seo.meta_title || (productDetail?.product_name ?? ''),
        metaDescription:
          seo.meta_description || stripHtml(productDetail?.product_description ?? ''),
        url,
        robots: parseRobotsTag(seo.robots_tag),
      }

      const slug = slugRecommendation
        ? mapSlugRecommendation(slugRecommendation, productDetail?.slug ?? '')
        : null

      return { form, slug }
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(null)
    }
  },
)

export const updateSEO = createAsyncThunk(
  `${SLICE_NAME}/updateSEO`,
  async (arg: { withSlug?: boolean } | undefined, { getState, dispatch, rejectWithValue }) => {
    const withSlug = arg?.withSlug ?? false
    try {
      const {
        seo: { productId, formSEO, formSLUG },
      } = getState() as StoreStateType

      const payload: SEOUpdatePayloadType = {
        id: productId,
        seo: {
          meta_title: formSEO.metaTitle,
          meta_description: formSEO.metaDescription,
          canonical_url: formSEO.url,
          robots_tag: serializeRobotsTag(formSEO.robots),
        },
      }

      // include slug only when triggered from slug publish and there are changes.
      if (withSlug && formSLUG.changes.length > 0) payload.slug = formSLUG.label

      const { data } = await updateProductSEOApi(productId, payload)

      toastSuccess(data.message || 'Berhasil memperbarui SEO')

      // refetch to sync form with the persisted values.
      dispatch(fetchSEODetail(productId))

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(null)
    }
  },
)
